import PropTypes from 'prop-types';
import {observer, PropTypes as MobXPropTypes} from 'mobx-react';
import React from 'react';

import autobind from 'common/decorators/autobind.js';
import {
    PRACTICE_LOC_CONTACT_TYPE,
    PROFILE_CONTACT_TYPE,
    DIRECTORY_SEARCH_RESULT_CARD_LINK,
    BANNER_AD_CARD,
    PRACTICE_LOCATION_CARD,
    PRACTICE_GROUP_CARD,
    PRACTITIONER_GROUP_CARD,
    PROFILE_CARD,
    CTA_CALL_NOW,
} from 'core/constants.js';

import {DirectoryStoreContextV2} from 'core/stores/RootStore.js';
import {
    canShowMh1NewPatientSnippet,
    getBookingAvailabilityFromApi,
} from 'core/services/Mh1Service';

import CallNowBtn from 'core/components/v2/CallNowBtn.js';
import CustomBookingBtn from 'core/components/v2/CustomBookingBtn.js';
import MH1BookNowBtn from 'core/components/v2/MH1BookNowBtn.js';
import {
    CardV2,
    AvailabilitySnippet,
} from '@healthshareau/hs-component-library';
import {trackBannerDetail} from '../services/internalEventTracking';

export default
@observer
class BannerAd extends React.Component {
    static contextType = DirectoryStoreContextV2;
    static propTypes = {
        bannerType: PropTypes.string,
        bookingAvailability: PropTypes.shape({
            available: PropTypes.bool.isRequired,
            mh1: PropTypes.shape({
                practitionerId: PropTypes.string.isRequired,
                nextAvailable: PropTypes.string,
                slotId: PropTypes.string,
                newPatientsOnly: PropTypes.bool,
            }),
        }),
        bookingIntegration: PropTypes.shape({
            bookingId: PropTypes.oneOfType([
                PropTypes.string,
                PropTypes.number,
            ]),
            name: PropTypes.string,
            url: PropTypes.string,
            newPatientNextAvailableOnly: PropTypes.bool,
        }),
        bookingsEnabled: MobXPropTypes.arrayOrObservableArrayOf(
            PropTypes.string,
        ),
        customBookingDataUrl: PropTypes.string,
        displayPhoneNumber: PropTypes.bool,
        hasMedicalSpecialty: PropTypes.bool.isRequired,
        headline: PropTypes.string.isRequired,
        healthFund: PropTypes.shape({
            id: PropTypes.number,
            showRatings: PropTypes.bool,
        }),
        id: PropTypes.number.isRequired,
        isClient: PropTypes.bool,
        isPractitioner: PropTypes.bool,
        isMobileOrTablet: PropTypes.bool,
        isReferrals: PropTypes.bool,
        offersHicaps: PropTypes.bool,
        offersTelehealth: PropTypes.bool.isRequired,
        practiceData: MobXPropTypes.arrayOrObservableArrayOf(
            PropTypes.shape({
                address: PropTypes.string,
                id: PropTypes.number,
                localityName: PropTypes.string,
                localityState: PropTypes.string,
                name: PropTypes.string,
                phones: MobXPropTypes.arrayOrObservableArrayOf(
                    PropTypes.shape({
                        number: PropTypes.string,
                    }),
                ).isRequired,
                practiceUrl: PropTypes.string,
            }),
        ),
        ratings: PropTypes.shape({
            average: PropTypes.number.isRequired,
            count: PropTypes.number.isRequired,
        }),
        renderModal: PropTypes.func,
        avatar: PropTypes.string,
        searchAvatar: PropTypes.string,
        specialInterestsTagline: PropTypes.string,
        specialties: PropTypes.oneOfType([
            PropTypes.arrayOf(
                PropTypes.shape({
                    name: PropTypes.string.isRequired,
                }),
            ),
            PropTypes.object, // ObservableArray
        ]),
        tagline: PropTypes.string.isRequired,
        tagline2: PropTypes.string.isRequired,
        telehealthDirectory: PropTypes.bool,
        url: PropTypes.string.isRequired,
        userType: PropTypes.string,
    };

    static defaultProps = {
        displayPhoneNumber: true,
    };

    getContactId(type) {
        const {id, practitioners} = this.props;
        if (type === PROFILE_CONTACT_TYPE) {
            return practitioners[0].id;
        } else {
            return id;
        }
    }

    constructor(props) {
        super(props);

        this.state = {
            modalDisplayed: false,
        };
        const practiceData = props.practiceData || [];
        const type = props.isPractitioner
            ? PROFILE_CONTACT_TYPE
            : PRACTICE_LOC_CONTACT_TYPE;
        this.modalProps = {
            closeModal: this.closeModal,
            contactId: this.getContactId(type),
            contactName: props.headline,
            contactType: type,
            isEnhanced: true,
            items: practiceData.map((item) => {
                // item will have type if it's a practice list that has a
                // practice group as the first element
                if (!item.type) {
                    item.type = type;
                }
                return item;
            }),
        };
    }

    @autobind
    closeModal() {
        this.setState({modalDisplayed: false});
    }

    @autobind
    handlePhoneClick() {
        this.setState({modalDisplayed: true});
    }

    @autobind
    trackBannerDetailEvent() {
        trackBannerDetail(this.props.id);
    }

    getBookingsParam() {
        const {bannerType, practiceData, practitioners} = this.props;
        const param = {};
        let paramId;
        switch (bannerType) {
            case 'practice_group':
                if (practiceData.length > 0) {
                    paramId = practiceData[0]['id'];
                }
                param['practice_group'] = paramId;
                break;
            case 'practice_location':
                if (practiceData.length > 0) {
                    paramId = practiceData[0]['id'];
                }
                param['practice_location'] = paramId;
                break;
            case 'practitioners':
                if (practitioners.length > 0) {
                    paramId = practitioners[0]['id'];
                }
                param['practitioner'] = paramId;
                break;
            case 'practitioner_group':
                // A practitioner group is not a bookable entity (no group-level
                // booking integration), so it contributes no booking param.
                break;
        }
        return param;
    }

    getBannerUrl() {
        const {healthFund, url} = this.props;
        let bannerUrl = url;
        if (healthFund?.id) {
            bannerUrl += `?health_fund=${healthFund.id}`;
        }
        return bannerUrl;
    }

    getSpecialtyName(specialty) {
        const {
            rootStore: {
                paramStore: {isProUser, isReferrals},
            },
        } = this.context;
        if (isProUser || isReferrals) {
            return specialty.professionalName;
        } else {
            return specialty.name;
        }
    }

    renderSpecialties() {
        const {specialties} = this.props;
        if (!specialties.length) {
            return null;
        }
        return (
            <>
                {specialties
                    .map((specialty) => {
                        return this.getSpecialtyName(specialty);
                    })
                    .join(', ')}
            </>
        );
    }

    getPhoneNumber() {
        const {
            practiceData,
            isPractitioner,
            healthFund,
            hasMedicalSpecialty,
            displayPhoneNumber,
        } = this.props;
        if (!practiceData || !practiceData[0]?.phones.length) {
            return null;
        }
        const phone = practiceData[0]?.phones[0];
        if (!phone.number) {
            return null;
        }

        if (
            ((isPractitioner && healthFund?.id && hasMedicalSpecialty) ||
                displayPhoneNumber) &&
            displayPhoneNumber
        ) {
            return {
                phone: phone.number,
                gaSelector: 'call-now-button-secondary',
                gaObjectType: `${BANNER_AD_CARD} ${this.getObjectType()}`,
            };
        }
    }

    getFees() {
        const {
            hasMedicalSpecialty,
            healthFund,
            id,
            isPractitioner,
        } = this.props;
        if (isPractitioner && healthFund?.id && hasMedicalSpecialty) {
            return {
                feesUrl: `${this.getBannerUrl()}#fees`,
                gaObjectType: `${BANNER_AD_CARD} ${this.getObjectType()}`,
                gaSelector: 'see-out-of-pocket-costs',
            };
        }
        return null;
    }

    getRatingAverage() {
        const {healthFund, ratings} = this.props;
        const {
            rootStore: {
                paramStore: {isReferrals},
            },
        } = this.context;
        if (
            isReferrals ||
            !ratings ||
            (healthFund?.id && !healthFund?.showRatings)
        ) {
            return null;
        }

        const {average} = ratings;
        return average;
    }

    getRatingCount() {
        const {healthFund, ratings} = this.props;
        const {
            rootStore: {
                paramStore: {isReferrals},
            },
        } = this.context;
        if (
            isReferrals ||
            !ratings ||
            (healthFund?.id && !healthFund?.showRatings)
        ) {
            return;
        }

        const {count} = ratings;
        return count;
    }

    getObjectType() {
        const {bannerType} = this.props;
        let objectTypeStr;
        switch (bannerType) {
            case 'practice_group':
                objectTypeStr = PRACTICE_GROUP_CARD;
                break;
            case 'practice_location':
                objectTypeStr = PRACTICE_LOCATION_CARD;
                break;
            case 'practitioners':
                objectTypeStr = PROFILE_CARD;
                break;
            case 'practitioner_group':
                objectTypeStr = PRACTITIONER_GROUP_CARD;
                break;
        }
        return objectTypeStr;
    }

    renderBookingButton() {
        const {
            bookingAvailability, // mh1 data from API
            bookingIntegration, // booking integration settings
            customBookingDataUrl,
        } = this.props;

        const {
            rootStore: {
                paramStore: {hasSkin, dataSource, isClient, isLoggedInGP},
                healthFundStore: {
                    isHealthFundBookingsEnabled,
                    healthFundThemeColor,
                },
            },
        } = this.context;

        const colorTheme =
            hasSkin && healthFundThemeColor ? healthFundThemeColor : null;

        // Practitioner-group banners are not a bookable entity: they may carry
        // a phone in practiceData (for the display-only phone chip) but must
        // never render a Call Now / booking button. isPPHasPhone gates the
        // Call Now fallback below, so exclude PG here (also guards the access
        // for banner types that legitimately have no practiceData).
        const {bannerType} = this.props;
        const isPPHasPhone =
            bannerType !== 'practitioner_group' &&
            this.props.practiceData?.[0]?.phones?.length;

        const availability = getBookingAvailabilityFromApi(
            bookingAvailability,
        );

        // Banner Ads can carry MH1 booking data via `bookingAvailability`
        // without a separate `bookingIntegration` payload, so the button is
        // gated only on availability here.
        const showMh1BookNowBtn =
            isHealthFundBookingsEnabled && !!availability;

        const showMh1NewPatientSnippet =
            canShowMh1NewPatientSnippet({
                bookingIntegration,
                isHealthFundBookingsEnabled,
                hasSkin,
                isClient,
                isLoggedInGP,
            }) && !!availability;

        const showCustomBookingBtn =
            isHealthFundBookingsEnabled && !hasSkin && customBookingDataUrl;

        // [1] new-patient snippet (banner ad on directory)
        if (showMh1NewPatientSnippet) {
            return (
                <div className="hs-booking-button-v2">
                    <AvailabilitySnippet
                        availability={availability}
                        newPatientNextAvailable
                        gaSelector="new-patient-next-available-only"
                        gaObjectType={`${BANNER_AD_CARD} ${this.getObjectType()}`}
                    />
                    <CallNowBtn
                        colorTheme={colorTheme}
                        gaObjectType={`${BANNER_AD_CARD} ${this.getObjectType()}`}
                        modalContent={{...this.modalProps, colorTheme}}
                    />
                </div>
            );
        }

        // [2] generic MH1 Book Now button
        if (showMh1BookNowBtn) {
            return (
                <MH1BookNowBtn
                    dataSource={dataSource}
                    bookingIntegration={bookingIntegration}
                    availability={availability}
                    isMh1Running={true}
                    colorTheme={colorTheme}
                    gaObjectType={`${BANNER_AD_CARD} ${this.getObjectType()}`}
                />
            );
        }

        // [3] show custom booking button
        if (showCustomBookingBtn) {
            return (
                <CustomBookingBtn
                    colorTheme={colorTheme}
                    gaObjectType={`${BANNER_AD_CARD} ${this.getObjectType()}`}
                    customBookingDataUrl={customBookingDataUrl}
                />
            );
        }

        // [4] fall back to Call Now button if practitioner has a phone number
        if (isPPHasPhone) {
            return (
                <CallNowBtn
                    colorTheme={colorTheme}
                    gaObjectType={`${BANNER_AD_CARD} ${this.getObjectType()}`}
                    modalContent={{...this.modalProps, colorTheme}}
                />
            );
        }
    }

    getAddress() {
        const {practiceData, url} = this.props;
        const address = practiceData?.[0]?.address;
        if (practiceData && address) {
            return {
                addressText: address,
                gaObjectType: `${BANNER_AD_CARD} ${this.getObjectType()}`,
                gaSelector: 'address-link',
                url: url && `${url}#location`,
            };
        }
    }

    renderMainContent() {
        const {
            avatar,
            bannerType,
            headline,
            offersHicaps,
            offersTelehealth,
            searchAvatar,
            telehealthDirectory,
            tagline,
            tagline2,
            specialInterestsTagline,
        } = this.props;
        const {
            rootStore: {
                paramStore: {hasSkin},
                healthFundStore: {healthFundThemeColor},
            },
        } = this.context;
        let cardTagline;

        if (tagline && tagline2) {
            cardTagline = `${tagline} ${tagline2}`;
        } else {
            cardTagline = tagline || tagline2;
        }

        const directoryCardProps = {
            alt: headline,
            avatar:
                searchAvatar || avatar ? {src: searchAvatar || avatar} : {},
            button: this.renderBookingButton(),
            customClass:
                this.getObjectType() === PRACTICE_GROUP_CARD
                    ? 'square-shape'
                    : '',
            feesChip: this.getFees(),
            addressChip: this.getAddress(),
            specialInterests: specialInterestsTagline,
            phoneChip: this.getPhoneNumber(),
            tagline: cardTagline,
            header: headline,
            headingLevel: 2,
            featured: true,
            feesColor: healthFundThemeColor,
            offersTelehealth: telehealthDirectory || offersTelehealth,
            offersHicaps: offersHicaps,
            href: this.getBannerUrl(),
            rating: this.getRatingAverage(),
            numRatings: this.getRatingCount(),
            subheader: this.renderSpecialties(),
            gaObjectType: `${BANNER_AD_CARD} ${this.getObjectType()}`,
            gaSelector: DIRECTORY_SEARCH_RESULT_CARD_LINK,
            colorTheme:
                hasSkin && healthFundThemeColor ? healthFundThemeColor : null,
            onHeaderClick: () => this.trackBannerDetailEvent(),
            onPhoneClick: () => this.handlePhoneClick(),
        };

        return (
            <>
                <CardV2 {...directoryCardProps} />
            </>
        );
    }

    render() {
        const {renderModal, telehealthDirectory} = this.props;
        const {
            rootStore: {
                healthFundStore: {healthFundThemeColor},
                paramStore: {hasSkin},
            },
        } = this.context;
        const colorTheme =
            hasSkin && healthFundThemeColor ? healthFundThemeColor : null;
        const contactModalProps = {...this.modalProps, colorTheme};

        return (
            <li
                className={`cell ${
                    telehealthDirectory ? 'telehealth-directory' : ''
                }`.trim()}
            >
                {this.state.modalDisplayed && renderModal(contactModalProps)}
                {this.renderMainContent()}
            </li>
        );
    }
}
